import React from 'react';
import { NavLink } from 'react-router-dom';
import { Compass, Map, BookOpen, User, Route } from 'lucide-react';
import { motion } from 'framer-motion';
import { useStore } from '../store/useStore';
import { useI18n } from '../i18n';
import { useTheme } from '../themes';

interface BottomNavProps {
  className?: string;
}

const BottomNav: React.FC<BottomNavProps> = ({ className = '' }) => {
  const { user, entries } = useStore();
  const { t } = useI18n();
  const { isSkeuomorphism, isNeubrutalism } = useTheme();

  // Only show Trails if user has shifts
  const hasShifts = entries && entries.length > 0;

  const navItems = [
    { path: '/', label: t.nav.feed, icon: Compass },
    { path: '/map', label: t.nav.map, icon: Map },
    ...(user ? [
      { path: '/my-shifts', label: t.nav.myShifts, icon: BookOpen },
      ...(hasShifts ? [{ path: '/trails', label: t.nav.trails, icon: Route }] : []),
    ] : []),
    { path: user ? '/profile' : '/login', label: user ? t.nav.profile : t.nav.signIn, icon: User },
  ];

  // Theme-specific container classes
  const getContainerClasses = () => {
    if (isNeubrutalism) {
      return 'bg-white dark:bg-neutral-900 border-t-4 border-black dark:border-white';
    }
    if (isSkeuomorphism) {
      return 'bg-gradient-to-t from-amber-50/98 to-amber-50/95 dark:from-neutral-900/98 dark:to-neutral-800/95 border-t-2 border-amber-200/50 dark:border-amber-800/30';
    }
    return 'glass rounded-2xl shadow-premium border border-white/10 dark:border-neutral-800/50';
  };

  // Theme-specific active item classes
  const getActiveClasses = (isActive: boolean) => {
    if (isActive) {
      if (isNeubrutalism) {
        return 'text-black dark:text-white bg-yellow-400 border-2 border-black dark:border-white';
      }
      if (isSkeuomorphism) {
        return 'text-amber-800 dark:text-amber-300 bg-amber-100/80 dark:bg-amber-900/30';
      }
      return 'text-brand-600 dark:text-brand-400 bg-brand-50/50 dark:bg-brand-900/20';
    }
    return 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300';
  };

  return (
    <motion.nav 
      initial={{ y: 100 }}
      animate={{ y: 0 }}
      className={`fixed bottom-0 left-0 right-0 z-40 md:hidden bottom-nav ${className}`}
    >
      <div className={isNeubrutalism ? '' : 'mx-3 mb-3'}>
        <div className={getContainerClasses()}>
          <div className="flex items-center justify-around px-1 py-2">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `
                  flex flex-col items-center justify-center py-2 px-2 transition-all duration-200
                  ${isNeubrutalism ? '' : 'rounded-xl'}
                  ${getActiveClasses(isActive)}
                `}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-[10px] mt-1 font-medium truncate max-w-[56px]">
                  {item.label}
                </span>
              </NavLink>
            ))}
          </div>
        </div>
      </div>
    </motion.nav>
  );
};

export default BottomNav;
